import {FIELD_FLIGHT_SOUNDS} from '../configs/fieldAmbienceSounds';

/**
 * ГОЛОСА ПТИЦ: пролет стаи или орла иногда озвучен криком этих птиц (см.
 * configs/fieldAmbienceSounds).
 *
 * Когда пролет начинается, бросается жребий: будет ли звук и в какой момент.
 * Момент — доля времени, которое птицы проведут В КАДРЕ игрока, и отсчет идет,
 * только пока они в кадре: стая, летящая за краем экрана, молчит, а пролет,
 * который игрок так и не увидел, остается без голоса.
 *
 * Крик длится несколько секунд — дольше, чем птицы иной раз остаются на виду.
 * Птицы ушли из кадра, а он еще звучит (улетели, растаяли, игрок увел камеру)
 * — крик плавно затихает и обрывается; вернулись, пока он не затих, —
 * возвращается. Подгонять момент под длину крика (чтобы он успевал отзвучать)
 * нельзя: на небольшом экране стая в кадре столько же, сколько длится крик, и
 * момент перестал бы быть случайным — крик раздавался бы, едва стая покажется.
 *
 * Отсчет ведется ТЕМ ЖЕ временем, что двигает птиц (шаг времени кадра от
 * FieldAmbience), а не настенными часами: в скрытой вкладке полет стоит —
 * стоит и отсчет.
 *
 * Звуки — отложенные: если они еще не загружены (или звук в игре выключен),
 * SoundManager.playDeferredSound молча ничего не играет.
 *
 * Жребий здесь свой, а не общий с антуражем: звук не должен менять то, как
 * летят птицы и плывут облака.
 *
 * Голоса — украшение украшения: сбой в них не должен задеть антураж, поэтому
 * наружу отсюда ничего не выходит.
 */

/** Оборвать звучащий крик; сбой на одном не должен оставить звучать остальные */
const stopSound = (sound) => {
  try {
    sound.stop();
  } catch (error) {
    console.warn('[FieldAmbienceSounds] Не удалось остановить голос птиц', error);
  }
};

// Звучащим криком можно управлять, если SoundManager отдал его целиком
const SOUND_CONTROLS = ['checkIfPlaying', 'setLevel', 'stop'];

export default class FieldAmbienceSounds {
  /**
   * @param {Phaser.Scene} scene — сцена с soundManager
   * @param {Object} [options]
   * @param {function(): number} [options.rng] — источник случайности (в тестах — заглушка)
   */
  constructor(scene, {rng = Math.random} = {}) {
    this.scene = scene;
    this.rng = rng;
    // Назначенные крики: {remainingMs, soundId, checkIfAlive, checkIfInView}
    this.pending = [];
    // Звучащие крики: {sound, level, isReleased, checkIfAlive, checkIfInView};
    // sound — звук от SoundManager.playDeferredSound, level — слышимость от 0
    // до 1: падает, пока птиц не видно
    this.playing = [];
  }

  /**
   * Пролет начался: решить, будет ли он озвучен и когда
   * @param {Object} params
   * @param {string[]} params.soundIds — голоса этих птиц; пусто — молчат
   * @param {function(): number} params.resolveInViewMs — сколько времени птицы
   *   проведут в кадре игрока (оценка наперед, см. utils/effects/flightView);
   *   0 — по расчету в кадр не попадут. Спрашивается, только если пролет
   *   озвучен
   * @param {function(): boolean} [params.checkIfAlive] — идет ли пролет еще:
   *   птиц могли убрать раньше срока (поле перестроили, гора исчезла)
   * @param {function(): boolean} [params.checkIfInView] — видит ли игрок птиц
   *   прямо сейчас
   * @returns {boolean} назначен ли крик
   */
  schedule({soundIds, resolveInViewMs, checkIfAlive = () => true, checkIfInView = () => true} = {}) {
    try {
      if (!soundIds?.length || typeof resolveInViewMs !== 'function') {
        return false;
      }
      if (this.rng() >= FIELD_FLIGHT_SOUNDS.chance) {
        return false;
      }
      const inViewMs = resolveInViewMs();
      if (!Number.isFinite(inViewMs) || inViewMs < 0) {
        return false;
      }
      const {startShare: {min, max}, minViewWindowMs} = FIELD_FLIGHT_SOUNDS;
      const share = this.rng();
      const soundId = soundIds[Math.min(soundIds.length - 1, Math.floor(this.rng() * soundIds.length))];
      // Камеру могут подвести и к птицам, которых по расчету не увидеть: тогда
      // крик раздается вскоре после того, как они показались
      const windowMs = Math.max(inViewMs, minViewWindowMs);
      const remainingMs = windowMs * (min + (max - min) * share);
      this.pending.push({remainingMs, soundId, checkIfAlive, checkIfInView});
      return true;
    } catch (error) {
      console.warn('[FieldAmbienceSounds] Не удалось назначить голос птиц', error);
      return false;
    }
  }

  /**
   * Шаг времени полета: отсчет идет у тех, кого игрок сейчас видит; у кого
   * подошел срок — кричит. Птиц, которых уже нет, ждать незачем. Звучащий крик
   * затихает, пока его птиц не видно
   * @param {number} stepMs — шаг времени кадра (тот же, что двигает птиц)
   */
  update(stepMs) {
    if (!(stepMs > 0) || (this.pending.length === 0 && this.playing.length === 0)) {
      return;
    }
    try {
      this.updatePlaying(stepMs);
      const due = [];
      this.pending = this.pending.filter((entry) => {
        if (!entry.checkIfAlive()) {
          return false;
        }
        if (!entry.checkIfInView()) {
          return true;
        }
        entry.remainingMs -= stepMs;
        if (entry.remainingMs > 0) {
          return true;
        }
        due.push(entry);
        return false;
      });
      due.forEach((entry) => this.play(entry));
    } catch (error) {
      console.warn('[FieldAmbienceSounds] Не удалось сыграть голос птиц', error);
      this.pending = [];
      this.stopPlaying();
    }
  }

  /** Срок подошел: крик звучит; дальше за его слышимостью следит updatePlaying */
  play({soundId, checkIfAlive, checkIfInView}) {
    const sound = this.scene?.soundManager?.playDeferredSound?.(soundId);
    // Звук не сыгран (не загружен, выключен) или им нельзя управлять — следить не за чем
    if (!sound || SOUND_CONTROLS.some((control) => typeof sound[control] !== 'function')) {
      return;
    }
    this.playing.push({sound, level: 1, isReleased: false, checkIfAlive, checkIfInView});
  }

  /** Звучащие крики: пока птиц не видно, крик затихает; затих — обрывается */
  updatePlaying(stepMs) {
    if (this.playing.length === 0) {
      return;
    }
    const {fadeOutMs} = FIELD_FLIGHT_SOUNDS;
    this.playing = this.playing.filter((voice) => {
      // Доиграл сам — следить больше не за чем
      if (!voice.sound.checkIfPlaying()) {
        return false;
      }
      const isHeard = !voice.isReleased && voice.checkIfAlive() && voice.checkIfInView();
      const levelStep = stepMs / fadeOutMs;
      const level = Math.min(1, Math.max(0, voice.level + (isHeard ? levelStep : -levelStep)));
      if (level === voice.level) {
        return true;
      }
      voice.level = level;
      if (level <= 0) {
        stopSound(voice.sound);
        return false;
      }
      voice.sound.setLevel(level);
      return true;
    });
  }

  /** Оборвать все звучащие крики разом */
  stopPlaying() {
    const playing = this.playing;
    this.playing = [];
    playing.forEach(({sound}) => stopSound(sound));
  }

  /**
   * Птиц убрали (поле перестроили): назначенные крики отменяются, звучащие —
   * затихают, как за улетевшими птицами
   */
  clear() {
    this.pending = [];
    this.playing.forEach((voice) => {
      voice.isReleased = true;
    });
  }

  /** Сцена уходит: шагов времени больше не будет — звучащие крики обрываются */
  destroy() {
    this.pending = [];
    this.stopPlaying();
  }
}
